import { Component, OnDestroy, OnInit } from '@angular/core';
import { faFileDownload } from '@fortawesome/pro-duotone-svg-icons';
import { faKey, faSignInAlt, faStar } from '@fortawesome/pro-regular-svg-icons';
import { faSignInAlt as faSignInAltSolid, faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { GitVersionInfo } from '../../../../generated/signalR/git-version-info';
import { SessionCookieService } from '../../../services/session-cookie.service';
import { UpdateService } from '../../../services/update/update.service';
import { CommunicationFailureSelectors } from '../../../store/server-communication/communication-failure.selectors';
import { Sccs1ConnectionState } from '../../../store/server-communication/sccs1-connection/sccs1-connection.state';
import { IUserSessionMonitoringInfo, ServerCommunicationState } from '../../../store/server-communication/server-communication.state';
import { SamwinVersion } from '../../../store/utility/samwin-version/samwin-version';

@Component({
   selector: 'samweb-system-monitor-overview',
   templateUrl: './system-monitor-overview.component.html',
   styleUrls: ['./system-monitor-overview.component.scss'],
})
export class SystemMonitorOverviewComponent implements OnInit, OnDestroy {
   public keyIcon = faKey;
   public loggedOnIcon = faSignInAltSolid;
   public notLoggedOnIcon = faSignInAlt;
   public inUseIcon = faStarSolid;
   public notInUseIcon = faStar;
   public downloadLogIcon = faFileDownload;

   public isExtendedMode: boolean;

   @Select(CommunicationFailureSelectors.isCommunicationHealthy)
   public isCommunicationHealthy$: Observable<boolean>;

   @Select(CommunicationFailureSelectors.isRealTimeConnectionHealthy)
   public isRealTimeConnectionHealthy$: Observable<boolean>;

   @Select(CommunicationFailureSelectors.isSccs1ConnectionHealthy)
   public isSccs1ConnectionHealthy$: Observable<boolean>;

   public sessionId$: Observable<string>;

   public sessionInfos$: Observable<IUserSessionMonitoringInfo[]>;

   @Select(ServerCommunicationState.getSignalRConnectionId)
   public signalRConnectionId$: Observable<string>;

   @Select(ServerCommunicationState.getSignalRConnectionServerVersionInfo)
   public webApiVersion$: Observable<GitVersionInfo>;

   @Select(ServerCommunicationState.getUserVersionInfo)
   public userVersion$: Observable<GitVersionInfo>;

   @Select(Sccs1ConnectionState.getSccs1SessionInUse)
   public sccs1SessionId$: Observable<string>;

   @Select(ServerCommunicationState.getResponsibleHostForUser)
   public responsibleServer$: Observable<string>;

   @Select(ServerCommunicationState.getWebApiServerAddress)
   public connectedWebApiServer$: Observable<string>;

   @Select(Sccs1ConnectionState.getSccs1ConnectionInUse)
   public sccs1ConnectionInUse$: Observable<string>;

   @Select(Sccs1ConnectionState.getAllConnections)
   public sccs1Connections$: Observable<{ name: string; isAvailable: boolean }[]>;

   @Select(Sccs1ConnectionState.getConnectionWithSuccessfulLogons)
   public getConnectionWithSuccessfulLogons$: Observable<string[]>;

   @Select(Sccs1ConnectionState.isPreventSwitchOverActive)
   public isPreventSwitchOverActive$: Observable<boolean>;

   public isNewVersionAvailable$: Observable<boolean>;

   public clientVersion = SamwinVersion.VERSION;

   private destroy$ = new Subject();

   constructor(private store: Store, private sessionCookieService: SessionCookieService, private updateService: UpdateService) {}

   ngOnInit(): void {
      this.sessionId$ = this.sessionCookieService.sessionCookieValue$;
      this.isNewVersionAvailable$ = this.updateService.newVersionAvailable$;

      this.sessionInfos$ = combineLatest([this.sessionId$, this.store.select(ServerCommunicationState.getSessionInfos)]).pipe(
         switchMap(([sessionId, sessionInfos]) =>
            of(
               _(sessionInfos)
                  .sortBy([(s) => (s.sessionId === sessionId ? 0 : 1), (s) => s.sessionType.sortOrder, (s) => s.sessionId])
                  .value()
            )
         ),
         takeUntil(this.destroy$)
      );
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
